import { useCallback, useLayoutEffect, useState, type ReactNode } from "react";
import type { SkyTone } from "@/data/sky";
import { SKY_TONE_STORAGE_KEY, SkyThemeContext, isSkyTone } from "./sky-theme-core";

function getInitialTone(): SkyTone {
  if (typeof document !== "undefined" && isSkyTone(document.documentElement.dataset.skyTone)) {
    return document.documentElement.dataset.skyTone;
  }
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(SKY_TONE_STORAGE_KEY);
      if (isSkyTone(stored)) return stored;
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }
  return "sun";
}

export function SkyThemeProvider({ children }: { children: ReactNode }) {
  const [tone, setToneState] = useState<SkyTone>(getInitialTone);

  useLayoutEffect(() => {
    document.documentElement.dataset.skyTone = tone;
    try {
      window.localStorage.setItem(SKY_TONE_STORAGE_KEY, tone);
    } catch {
      // The in-memory theme still works when persistence is unavailable.
    }
  }, [tone]);

  const setTone = useCallback((nextTone: SkyTone) => {
    if (isSkyTone(nextTone)) setToneState(nextTone);
  }, []);

  return <SkyThemeContext.Provider value={{ tone, setTone }}>{children}</SkyThemeContext.Provider>;
}
